import { useState } from 'react'
import {
  Zap,
  Shield,
  Globe,
  Cpu,
  Users,
  Lock,
  Monitor,
  Wifi,
  FileText,
  MessageSquare,
  Video,
  Layers,
  Clock,
  Smartphone,
  Key,
  Eye,
  History,
  Terminal,
  Package,
  Cast,
  Server,
  ArrowUpRight,
  Check,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'
import { PageHeading, InnerClosing } from '../components/Interior'

interface FeatureText {
  title: string
  desc: string
}

interface CategoryText {
  title: string
  description: string
  headline: string
  features: FeatureText[]
}

// Icons per category / feature, in the same order as `categories` in features.json.
const categoryIcons: { icon: LucideIcon; features: LucideIcon[] }[] = [
  { icon: Zap, features: [Zap, Monitor, Wifi, Cpu] },
  { icon: Shield, features: [Lock, Key, Eye, History] },
  { icon: Users, features: [Users, MessageSquare, Video, FileText] },
  { icon: Globe, features: [Globe, Smartphone, Clock, Layers] },
  { icon: Server, features: [Terminal, Package, Cast, Layers] },
]

const screenFiles = [
  '02-masaustu.png',
  '05-izinler.png',
  '04-sohbet.png',
  '06-cihazlar.png',
  '03-dosyalar.png',
]
export default function Features() {
  const { t, i18n } = useTranslation(['features', 'common'])
  const [active, setActive] = useState(0)
  const categoryTexts = t('categories', { returnObjects: true }) as CategoryText[]
  const advancedFeatures = t('advanced', { returnObjects: true }) as string[]
  const featureCategories = categoryIcons.map((icons, index) => {
    const text = categoryTexts[index]
    return {
      ...text,
      icon: icons.icon,
      features: text.features.map((feature, i) => ({ ...feature, icon: icons.features[i] })),
    }
  })
  const category = featureCategories[active]
  return (
    <div className="designed-page features-page">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/features"
        structuredData={[schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.features'), url: '/features' }])]}
      />
      <PageHeading
        label={t('heading.label')}
        title={t('heading.title')}
        accent={t('heading.accent')}
        description={t('heading.description')}
      >
        <div className="feature-orbit" aria-hidden="true">
          <span>
            <Monitor size={25} />
          </span>
          <span>
            <Terminal size={25} />
          </span>
          <span>
            <Shield size={25} />
          </span>
          <span>
            <MessageSquare size={25} />
          </span>
          <img src="/brand/mark.svg" width="150" height="110" alt="" />
          <small>{t('heading.orbit')}</small>
        </div>
      </PageHeading>
      <section className="feature-explorer">
        <div className="feature-tabs" aria-label={t('tabsLabel')}>
          {featureCategories.map((item, index) => (
            <button
              aria-pressed={active === index}
              onClick={() => setActive(index)}
              key={item.title}
            >
              <item.icon size={18} />
              {item.title}
            </button>
          ))}
        </div>
        <div className="feature-story">
          <div className="feature-story-copy">
            <span className="eyebrow">
              0{active + 1} / {category.title.toLocaleUpperCase(i18n.language)}
            </span>
            <h2>{category.headline}</h2>
            <p>{category.description}</p>
            <Link
              className="text-link"
              to={active === 1 ? '/security' : '/download'}
            >
              {active === 1 ? t('story.securityLink') : t('story.startLink')}
              <ArrowUpRight size={17} />
            </Link>
          </div>
          <figure>
            <img
              src={'/product/' + screenFiles[active]}
              width="1920"
              height="1080"
              alt={t('story.previewAlt', { category: category.title })}
            />
            <figcaption>{t('story.previewCaption')}</figcaption>
          </figure>
        </div>
        <div className="feature-detail-grid">
          {category.features.map((item) => (
            <article key={item.title}>
              <item.icon size={23} />
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="feature-index">
        <div>
          <span className="eyebrow">{t('index.label')}</span>
          <h2>
            {t('index.titleLine1')}
            <br />
            {t('index.titleLine2')}
          </h2>
          <p>{t('index.text')}</p>
        </div>
        <ul>
          {advancedFeatures.map((feature) => (
            <li key={feature}>
              <Check size={17} />
              {feature}
            </li>
          ))}
        </ul>
      </section>
      <InnerClosing
        title={t('closing.title')}
        text={t('closing.text')}
      />
    </div>
  )
}
